import type { Item, ItemView } from '@hugo/core'
import type { LucideIcon } from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useRef } from 'react'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { useItemActions } from '@/lib/item-actions'
import { useItems, useSettings } from '@/lib/items'
import { isTypingTarget, overlayOpen } from '@/lib/keys'
import { type ListFilter, matchesFilter } from '@/lib/lists'
import { useUserNow } from '@/lib/session'
import { hasTag } from '@/lib/tags'
import { ItemRow, type RowMode } from './item-row'
import { ItemToolbar } from './item-toolbar'
import { View } from './view'
import { useWorkspace } from './workspace'

const MODE: Record<ItemView, RowMode> = {
  inbox: 'inbox',
  open: 'list',
  tasks: 'list',
  owe: 'list',
  waiting: 'list',
  snoozed: 'snoozed',
  done: 'done',
}

/**
 * A list of items with keyboard control: j/k or arrows move, Enter opens details, c captures,
 * and per-view action keys (inbox: t o w f x s e, plus a d to accept or dismiss hugo's
 * suggestion; lists: e x s t o w; done: r).
 */
export function ItemsView({
  view,
  filter = 'all',
  tag,
  name,
  title,
  description,
  empty,
  header,
}: {
  view: ItemView
  /** Show part of the list: what someone waits on the user for, or what they wait on. */
  filter?: ListFilter
  /** Only the items with this tag. */
  tag?: string
  /** The page's name for `data-view` (default: the view). */
  name?: string
  title: string
  description: string
  empty: { title: string; text: string; icon: LucideIcon }
  header?: ReactNode
}) {
  const query = useItems(view)
  const settings = useSettings()
  const timeZone = settings.data?.timezone ?? 'Europe/Zurich'
  const now = useUserNow(30_000)
  const { selected, setSelected, openDetails, setCaptureOpen, setSnoozeOpen } = useWorkspace()
  const actions = useItemActions()
  const all = query.data
  const items = useMemo(
    () => (all ?? []).filter((item) => matchesFilter(item, filter) && hasTag(item, tag)),
    [all, filter, tag],
  )
  const mode = MODE[view]
  const lastIndex = useRef(0)
  const current = selected ? (items.find((i) => i.id === selected.id) ?? null) : null

  // Keep a selection: after filing an item the next one is selected.
  useEffect(() => {
    if (items.length === 0) {
      if (selected) setSelected(null)
      return
    }
    const index = selected ? items.findIndex((i) => i.id === selected.id) : -1
    if (index >= 0) {
      lastIndex.current = index
      if (items[index] !== selected) setSelected(items[index] ?? null)
      return
    }
    setSelected(items[Math.min(lastIndex.current, items.length - 1)] ?? null)
  }, [items, selected, setSelected])

  // Clear the selection when leaving the view.
  useEffect(() => () => setSelected(null), [setSelected])

  useEffect(() => {
    if (!selected) return
    document.querySelector(`[data-item-id="${selected.id}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [selected])

  useEffect(() => {
    const select = (item: Item | undefined) => {
      if (item) setSelected(item)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return
      if (isTypingTarget(event.target) || overlayOpen()) return
      const index = current ? items.findIndex((i) => i.id === current.id) : -1
      const key = event.key
      if (key === 'j' || key === 'ArrowDown') select(items[Math.min(index + 1, items.length - 1)])
      else if (key === 'k' || key === 'ArrowUp') select(items[Math.max(index - 1, 0)])
      else if (key === 'c') setCaptureOpen(true)
      else if (!current) return
      else if (key === 'Enter') openDetails(current.id)
      else if (key === 's' && mode !== 'done') setSnoozeOpen(true)
      else if (key === 'e' && mode !== 'done') void actions.complete(current)
      else if (mode === 'inbox' && key === 't') void actions.triage(current, 'task')
      else if (mode === 'inbox' && key === 'o') void actions.triage(current, 'owe')
      else if (mode === 'inbox' && key === 'w') void actions.triage(current, 'waiting')
      else if (mode === 'inbox' && key === 'f') void actions.triage(current, 'fyi')
      else if (mode === 'inbox' && key === 'x') void actions.triage(current, 'ignore')
      // Only with a suggestion; otherwise the keys do nothing (no preventDefault either).
      else if (mode === 'inbox' && key === 'a' && current.suggestion) void actions.accept(current)
      else if (mode === 'inbox' && key === 'd' && current.suggestion)
        void actions.dismissSuggestion(current)
      else if (mode === 'list' && key === 't') void actions.move(current, 'task')
      else if (mode === 'list' && key === 'o') void actions.move(current, 'owe')
      else if (mode === 'list' && key === 'w') void actions.move(current, 'waiting')
      else if ((mode === 'list' || mode === 'snoozed') && key === 'x') void actions.drop(current)
      else if (mode === 'done' && key === 'r') void actions.reopen(current)
      else return
      event.preventDefault()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [items, current, mode, actions, setSelected, openDetails, setCaptureOpen, setSnoozeOpen])

  const state = query.isPending ? 'loading' : query.isError ? 'error' : 'ready'
  return (
    <View
      name={name ?? view}
      state={state}
      title={title}
      description={description}
      className="pb-36 md:pb-6"
    >
      {header}
      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : items.length === 0 ? (
        <Empty className="flex-none border border-dashed py-16" data-testid="empty-state">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <empty.icon aria-hidden="true" />
            </EmptyMedia>
            <EmptyTitle>{empty.title}</EmptyTitle>
            <EmptyDescription>{empty.text}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <ul
          aria-label={title}
          className="divide-y overflow-hidden rounded-xl border"
          data-testid="item-list"
        >
          {items.map((item) => (
            <li key={item.id}>
              <ItemRow
                item={item}
                mode={mode}
                selected={current?.id === item.id}
                now={now}
                timeZone={timeZone}
                onSelect={setSelected}
                onOpen={(it) => openDetails(it.id)}
                onToggleDone={(it) => void actions.complete(it)}
              />
            </li>
          ))}
        </ul>
      )}
      {items.length > 0 ? <ItemToolbar mode={mode} item={current} timeZone={timeZone} /> : null}
    </View>
  )
}
